"use client";

import Link from "next/link";
import React from "react";

import { usePathname, useSearchParams } from "next/navigation";
import { useMemo } from "react";

function cn(...classes: (string | undefined | boolean)[]) {
    return classes.filter(Boolean).join(" ");
}

interface PaginationProps {
    children: React.ReactNode;
}
export function Pagination({ children }: PaginationProps) {
    return <nav className="flex items-center gap-2">{children}</nav>;
}

interface PaginationContentProps {
    children: React.ReactNode;
}
export function PaginationContent({ children }: PaginationContentProps) {
    return (
        <ul className="flex items-center gap-2 list-none p-0 m-0">{children}</ul>
    );
}

interface PaginationItemProps {
    children: React.ReactNode;
}
export function PaginationItem({ children }: PaginationItemProps) {
    return <li>{children}</li>;
}

export function PaginationEllipsis() {
    return <span className="px-2">...</span>;
}

interface PaginationLinkProps {
    href?: string;
    isActive?: boolean;
    children: React.ReactNode;
    [key: string]: any; // اجازه‌ی مقادیر اضافی
}

export function PaginationLink({
    href,
    isActive,
    children,
    ...rest
}: PaginationLinkProps) {
    const classes = [
        "px-3 py-1 border rounded hover:bg-gray-50 transition-colors",
        isActive ? "bg-blue-500 text-white" : ""
    ].join(" ");

    if (!href) {
        // اینجا فرض می‌کنیم باقی پراپ‌ها مختص Button هستند
        return (
            <button type="button" className={classes} {...rest}>
                {children}
            </button>
        );
    }

    // اینجا باقی پراپ‌ها مختص Link (Anchor) هستند
    return (
        <Link href={href} className={classes} {...rest}>
            {children}
        </Link>
    );
}

// دکمه قبلی
interface PaginationPreviousProps {
    href?: string;
    onClick?: () => void;
}
export function PaginationPrevious({ href, onClick }: PaginationPreviousProps) {
    if (href) {
        return (
            <Link
                href={href}
                className="px-3 py-1 border rounded hover:bg-gray-50 transition-colors"
            >
                قبلی
            </Link>
        );
    }
    return (
        <button
            onClick={onClick}
            className="px-3 py-1 border rounded hover:bg-gray-50 transition-colors"
        >
            قبلی
        </button>
    );
}

// دکمه بعدی
interface PaginationNextProps {
    href?: string;
    onClick?: () => void;
}
export function PaginationNext({ href, onClick }: PaginationNextProps) {
    if (href) {
        return (
            <Link
                href={href}
                className="px-3 py-1 border rounded hover:bg-gray-50 transition-colors"
            >
                بعدی
            </Link>
        );
    }
    return (
        <button
            onClick={onClick}
            className="px-3 py-1 border rounded hover:bg-gray-50 transition-colors"
        >
            بعدی
        </button>
    );
}


type Props = {
    /** تعداد کل صفحات */
    total: number;
    /** شماره صفحهٔ فعلی (اختیاری). در صورت عدم ارسال، از URL خوانده می‌شود */
    pages?: number;
    /** تابعی برای کنترل کامل (اگر بخواهید خودتان دستکاری کنید) */
    onChange?: (page: number) => void;
    /** آیا لینک با href بسازیم یا خیر */
    useHref?: boolean;
};

function generatePages(current: number, total: number) {
    const delta = 2;
    const range: (number | "...")[] = [];

    for (
        let i = Math.max(2, current - delta);
        i <= Math.min(total - 1, current + delta);
        i++
    ) {
        range.push(i);
    }

    if (current - delta > 2) {
        range.unshift("...");
    }
    if (current + delta < total - 1) {
        range.push("...");
    }

    range.unshift(1);
    if (total > 1) {
        range.push(total);
    }

    return range;
}

export default function PaginationScreen({
    total,
    pages,
    onChange,
    useHref = true
}: Props) {
    const pathname = usePathname();
    const searchParams = useSearchParams();

    const currentPage = useMemo(() => {
        if (pages) return pages;
        const fromURL = searchParams.get("page");
        return fromURL ? Number(fromURL) : 1;
    }, [pages, searchParams]);

    if (!total || total <= 1) return null;

    const pageNumbers = generatePages(currentPage, total);

    const buildHref = (page: number) => {
        const params = new URLSearchParams(searchParams.toString());
        params.set("page", page.toString());
        return `${pathname}?${params.toString()}`;
    };

    const handlePageChange = (page: number) => {
        onChange?.(page);
    };

    return (
        <div className="my-5 mx-auto table">
            <Pagination>
                <PaginationContent>
                    {currentPage > 1 && (
                        <PaginationItem>
                            {useHref ? (
                                <PaginationPrevious href={buildHref(currentPage - 1)} />
                            ) : (
                                <PaginationPrevious
                                    onClick={() => handlePageChange(currentPage - 1)}
                                />
                            )}
                        </PaginationItem>
                    )}

                    {pageNumbers.map((number, index) =>
                        number === "..." ? (
                            <PaginationItem key={`ellipsis-${index}`}>
                                <PaginationEllipsis />
                            </PaginationItem>
                        ) : (
                            <PaginationItem key={number}>
                                {useHref ? (
                                    <PaginationLink
                                        isActive={number === currentPage}
                                        href={buildHref(number)}
                                    >
                                        {number}
                                    </PaginationLink>
                                ) : (
                                    <PaginationLink
                                        isActive={number === currentPage}
                                        onClick={() => handlePageChange(number as number)}
                                    >
                                        {number}
                                    </PaginationLink>
                                )}
                            </PaginationItem>
                        )
                    )}

                    {currentPage < total && (
                        <PaginationItem>
                            {useHref ? (
                                <PaginationNext href={buildHref(currentPage + 1)} />
                            ) : (
                                <PaginationNext
                                    onClick={() => handlePageChange(currentPage + 1)}
                                />
                            )}
                        </PaginationItem>
                    )}
                </PaginationContent>
            </Pagination>
        </div>
    );
}
